import React, { Fragment, useMemo, useState } from 'react'
import { injectIntl, InjectedIntlProps } from 'react-intl'
import { isEmpty, partialRight } from 'lodash'

import { Attraction, DividedAttractions } from 'src/interfaces/Attraction'

import { EventSection } from '../eventSection'
import {
  ATTRACTIONS_ID,
  ATTRACTIONS_TITLE,
} from '../constants'

import { AttractionModal } from './AttractionModal/AttractionModal'
import { HeadlinerAttractionsSubSection } from './HeadlinerAttractionsSubSection'
import { AttractionsSubSection } from './AttractionsSubSection'
import { getModifiedAttractions, getTitle } from './attractions-helper'


const style = require('./AttractionsSection.css')

interface Props {
  eventData: any,
}

const AttractionsSectionComponent = ({
  eventData,
  intl,
}: Props & InjectedIntlProps) => {
  const {
    attractionsMainTitle,
    attractionsHeadlinersTitle,
    attractionsLineupTitle,
    attractions,
  } = eventData || {}

  const { ALL, MAIN, HEADLINERS, LINEUP } = ATTRACTIONS_TITLE

  const [selectedAttraction, setSelectedAttraction] =
    useState<Attraction | null>(null)

  const hasIndividualAttractions = !isEmpty(attractions)

  const modifiedAttractions: DividedAttractions =
    useMemo(
      () => hasIndividualAttractions && getModifiedAttractions(attractions),
      [hasIndividualAttractions, attractions],
    )

  const isAttractionsDivided = !isEmpty(modifiedAttractions.headliners)
  const isAnyAttractionInLineup = !isEmpty(modifiedAttractions.lineup)

  const getSectionTitle =
    partialRight(getTitle, { hasLineupAttractions: isAnyAttractionInLineup, intl })

  const mainTitle = getSectionTitle(MAIN, attractionsMainTitle)

  const handleCloseAttractionModal = () => setSelectedAttraction(null)

  const createOnSelect = (attraction: Attraction) => () => setSelectedAttraction(attraction)

  const sectionProps = {
    headlinerAttractions: {
      title: getSectionTitle(HEADLINERS, attractionsHeadlinersTitle),
      attractions: modifiedAttractions.headliners,
      createOnSelect,
      singleSubSectionClass: !isAnyAttractionInLineup && style.attractionSingleSubSection,
    },
    lineupAttractions: {
      title: getSectionTitle(LINEUP, attractionsLineupTitle),
      attractions: modifiedAttractions.lineup,
      createOnSelect,
    },
    allAttractions: {
      title: getSectionTitle(ALL, attractionsMainTitle),
      attractions: modifiedAttractions.lineup,
      createOnSelect,
      singleSubSectionClass: style.attractionSingleSubSection,
    },
  }

  return hasIndividualAttractions && (
    <Fragment>
      {
        isAttractionsDivided ?
          <EventSection
            sectionId={ATTRACTIONS_ID}
            heading={mainTitle}
          >
            <div className={style.attractionsSectionContainer}>
              <HeadlinerAttractionsSubSection {...sectionProps.headlinerAttractions} />
              {
                isAnyAttractionInLineup &&
                  <AttractionsSubSection {...sectionProps.lineupAttractions} />
              }
            </div>
          </EventSection>
          :
          isAnyAttractionInLineup &&
            <EventSection sectionId={ATTRACTIONS_ID}>
              <div className={style.attractionsSectionContainer}>
                <AttractionsSubSection {...sectionProps.allAttractions} />
              </div>
            </EventSection>
      }
      {
        selectedAttraction && <AttractionModal
          selectedAttraction={selectedAttraction}
          onClose={handleCloseAttractionModal}
        />
      }
    </Fragment>
  )
}

export const AttractionsSection = injectIntl(AttractionsSectionComponent)
